import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import { PATH_APP } from "src/routes/paths";
import { postActionRoute } from "src/utils/postActionRoute";
import {
  setTarget,
  forceTakeover,
  cancelTakeover,
  acknowledgeRevoked,
  refreshPresence,
  refreshLastPayment,
  releaseAllOnPageHide,
  setTpvRouteActive,
  whenLeaseSettled,
} from "src/utils/mesaLeaseManager";
import { holderLabel, isLeaseRoute } from "src/utils/mesaLeaseCore";
import { revokedHandled, pruneExpiredLeases } from "src/redux/slices/mesaLeases";
import { mesaKicked } from "src/redux/actions/mesaKick";
import {
  cleanMesa,
  getMesaSuccess,
  setSelectedIdMesaSuccess,
  setZonaSelected,
} from "src/redux/slices/mesas";
import { resetCart, saveHistoryCheckout } from "src/redux/slices/product";
import useResponsive from "src/hooks/useResponsive";
import { getDeviceId } from "src/utils/deviceId";
import {
  discardHeldCheckout,
  draftLinesForMesa,
  isMesaCheckoutBlocked,
  openMesaCheckout,
  refreshMesaCheckoutInPlace,
} from "src/utils/mesaCheckoutLoader";
import { decideSelectionRestore, previousMesaAfterCancel } from "src/utils/mesaSelectionRestore";
import { peekMesaOpenOnTpvEntry, takeMesaOpenOnTpvEntry } from "src/utils/reopenTicket";

// Rutas en las que "estar en una mesa" cuenta como estar dentro de ella:
// plano TPV grande (con panel de la mesa seleccionada), carta camarero,
// pago y editor de producto/complementos del camarero.
// (La regla vive en mesaLeaseCore → isLeaseRoute, con sus tests.)

const isGridRoute = (pathname) => (pathname || "").startsWith(PATH_APP.tuCarta.tpv);

const validMesaId = (id) =>
  id != null && id !== -1 && id !== "-1" && id !== "00000000-0000-0000-0000-000000000000";

// Hidratación una vez por usuario aunque haya dos layouts montados en un swap.
let hydratedForUser = null;
// Restauración de la mesa seleccionada tras recargar: una vez por carga.
let selectionRestoreDone = false;
// Mesa que se estaba viendo al salir del TPV: al volver se reabre por la vía
// «lease primero» (con «cobrando» solo el aviso).
let reopenOnReturnId = null;
// Mesa seleccionada antes de la actual (para volver a ella al cancelar el
// forzado). Módulo: compartida por los dos layouts.
let previousSelectedId = -1;
let currentSelectedId = -1;

/**
 * Fase 1 TPV · puente entre el estado de la UI y el lease de mesa.
 *  - Pide el lease de la mesa en la que está el camarero y lo suelta al salir.
 *  - Si otro dispositivo le quita la mesa: guarda lo no comandado en el
 *    borrador de la mesa, sale al plano y avisa.
 *  - Diálogo "¿Forzar?" cuando la mesa se está cobrando en otro dispositivo.
 * Se monta en DashboardLayout y TpvCartaLayout (no pinta nada fuera del diálogo).
 */
export default function MesaLeaseBridge() {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const user = useSelector((s) => s.authJwt?.user);
  const selectedId = useSelector((s) => s.mesas?.selectedId);
  const mesas = useSelector((s) => s.mesas?.mesas);
  const revoked = useSelector((s) => s.mesaLeases?.revoked);
  const prompt = useSelector((s) => s.mesaLeases?.chargingPrompt);
  const lastSelectedId = useSelector((s) => s.mesas?.lastSelectedId);
  // Mismo corte que ProductItem: en móvil pulsar una mesa es entrar en ella.
  const isMobile = useResponsive("down", "sm");

  const pathname = location?.pathname;
  // En móvil el plano no muestra la mesa seleccionada: estar en el plano no
  // es estar dentro de ninguna mesa (se suelta su lease y su presencia).
  const insideMesaRoute = isLeaseRoute(pathname) && !(isMobile && isGridRoute(pathname));
  const targetMesaId =
    user?.id && insideMesaRoute && validMesaId(selectedId) ? selectedId : null;
  // Rutas del TPV (plano, carta, cobro…): solo en ellas existen el lease, su
  // heartbeat y la presencia (regla usuario 2026-10-07).
  const tpvRoute = isLeaseRoute(pathname);

  // Mesa anterior a la actual (volver a ella al cancelar un forzado).
  if (String(selectedId) !== String(currentSelectedId)) {
    if (validMesaId(currentSelectedId)) previousSelectedId = currentSelectedId;
    currentSelectedId = selectedId;
  }

  // ── Solo dentro del TPV ────────────────────────────────────────────
  // Primero (los efectos corren en orden): el resto ya ve la ruta correcta.
  const wasTpvRoute = useRef(tpvRoute);
  useEffect(() => {
    setTpvRouteActive(tpvRoute);
    const was = wasTpvRoute.current;
    wasTpvRoute.current = tpvRoute;
    if (was && !tpvRoute) {
      // Salir del TPV: lo no comandado queda en el borrador de la mesa y al
      // volver se reabre (el lease se suelta con el efecto de abajo).
      if (validMesaId(selectedId)) {
        dispatch(saveHistoryCheckout(selectedId));
        reopenOnReturnId = selectedId;
      }
      return;
    }
    if (!was && tpvRoute) {
      // Vuelta al TPV: presencia fresca y la mesa por la vía «lease primero».
      refreshPresence();
      const id = reopenOnReturnId;
      reopenOnReturnId = null;
      // Ticket reabierto: lo abre el efecto siguiente (no la mesa de antes).
      if (validMesaId(peekMesaOpenOnTpvEntry())) return;
      if (!selectionRestoreDone || !validMesaId(id) || String(id) !== String(selectedId)) return;
      const mesa = (mesas || []).find((m) => String(m.id) === String(id));
      if (mesa) openMesaCheckout(dispatch, store.getState, mesa, { waitLease: whenLeaseSettled, force: true });
    }
  }, [tpvRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ticket reabierto (utils/reopenTicket) ──────────────────────────
  // Tras «Reabrir ticket», al entrar en el TPV se abre la mesa reabierta
  // (zona + selección + cuenta) en lugar de la que se veía al salir o de la
  // recordada al recargar. Después del efecto de ruta (el lease necesita
  // saber que estamos en el TPV) y antes de la restauración tras recargar.
  // Si el plano aún no ha llegado, espera a que llegue.
  useEffect(() => {
    if (!tpvRoute || !(mesas || []).length) return;
    const id = peekMesaOpenOnTpvEntry();
    if (!validMesaId(id)) return;
    const mesa = mesas.find((m) => String(m.id) === String(id));
    if (!mesa) return;
    takeMesaOpenOnTpvEntry();
    selectionRestoreDone = true;
    if (String(store.getState()?.mesas?.selectedId) !== String(mesa.id)) {
      dispatch(setSelectedIdMesaSuccess(mesa.id));
    }
    dispatch(getMesaSuccess(mesa));
    if (mesa.zonaId != null) dispatch(setZonaSelected(mesa.zonaId));
    openMesaCheckout(dispatch, store.getState, mesa, { waitLease: whenLeaseSettled, force: true });
  }, [tpvRoute, mesas]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Adquirir / soltar ───────────────────────────────────────────────
  useEffect(() => {
    setTarget(targetMesaId, { graceful: !targetMesaId });
  }, [targetMesaId]);

  useEffect(
    () => () => {
      // Desmontaje del layout: soltar con margen (el otro layout la pedirá).
      setTarget(null, { graceful: true });
    },
    [],
  );

  // ── Hidratación, limpieza de caducados y cierre de pestaña ─────────
  useEffect(() => {
    // Fuera del TPV no hace falta la presencia (se pide al entrar).
    if (!user?.id || !tpvRoute || hydratedForUser === user.id) return;
    hydratedForUser = user.id;
    refreshPresence();
    refreshLastPayment();
  }, [user?.id, tpvRoute]);

  useEffect(() => {
    const id = setInterval(() => dispatch(pruneExpiredLeases()), 15_000);
    return () => clearInterval(id);
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener("pagehide", releaseAllOnPageHide);
    return () => window.removeEventListener("pagehide", releaseAllOnPageHide);
  }, []);

  // ── Tras recargar: restaurar la mesa seleccionada o limpiar ────────
  // Nunca debe quedar el carrito con contenido sin mesa seleccionada.
  const mesasLoaded = (mesas || []).length > 0;
  useEffect(() => {
    // Solo dentro del TPV: recargar (o entrar) en Configuración no debe tomar
    // la mesa recordada; se restaura al entrar en el TPV.
    if (selectionRestoreDone || !user?.id || !mesasLoaded || !tpvRoute) return;
    selectionRestoreDone = true;
    if (validMesaId(selectedId)) return; // ya hay mesa elegida en esta carga
    (async () => {
      const state = store.getState();
      const mesa = validMesaId(lastSelectedId)
        ? (mesas || []).find((m) => String(m.id) === String(lastSelectedId)) || null
        : null;
      if (mesa) await refreshPresence();
      const decision = decideSelectionRestore({
        pendingId: lastSelectedId,
        mesa,
        presenceHolder: store.getState()?.mesaLeases?.byMesaId?.[lastSelectedId]?.holder || null,
        myDeviceId: getDeviceId(),
        hasDraft: draftLinesForMesa(state.product?.checkoutHistory, lastSelectedId).length > 0,
        cartHasLines: (state.product?.checkout?.cart || []).length > 0,
      });
      // El usuario pudo elegir otra mesa mientras tanto.
      if (validMesaId(store.getState()?.mesas?.selectedId)) return;
      const clear = () => {
        dispatch(resetCart());
        dispatch(cleanMesa());
        dispatch(setSelectedIdMesaSuccess(-1));
      };
      if (decision === "clear") clear();
      if (decision !== "restore") return;
      dispatch(setSelectedIdMesaSuccess(mesa.id));
      dispatch(getMesaSuccess(mesa));
      // Mostrar su zona: sin esto el plano abría en la primera zona y una
      // mesa de otra (p. ej. Terraza) no se veía seleccionada.
      if (mesa.zonaId != null) dispatch(setZonaSelected(mesa.zonaId));
      // Como al pulsar la mesa: primero el lease y solo si es nuestra se pinta
      // su cuenta (con «otro dispositivo cobrando» solo el aviso «¿Forzar?»).
      // Antes se cargaba la cuenta sin esperar al lease: se pintaba y el 409
      // llegaba después (parpadeo; al forzar, mesa seleccionada sin cuenta).
      const session = await openMesaCheckout(dispatch, store.getState, mesa, {
        waitLease: whenLeaseSettled,
        force: true,
      });
      // Bloqueada por el cobro de otro: la decide el aviso (forzar/cancelar).
      if (isMesaCheckoutBlocked(mesa.id)) return;
      const hasDraftNow = draftLinesForMesa(store.getState().product?.checkoutHistory, mesa.id).length > 0;
      const after = decideSelectionRestore({
        pendingId: mesa.id,
        mesa,
        hasDraft: hasDraftNow,
        sessionOpen: !!session,
      });
      // Mesa ya cobrada/cerrada y sin borrador: no queda nada que mostrar.
      if (after === "clear" && String(store.getState()?.mesas?.selectedId) === String(mesa.id)) clear();
    })();
  }, [user?.id, mesasLoaded, tpvRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Me han quitado la mesa ─────────────────────────────────────────
  const handledTs = useRef(null);
  useEffect(() => {
    if (!revoked || handledTs.current === revoked.ts) return;
    handledTs.current = revoked.ts;
    acknowledgeRevoked(revoked.mesaId);

    const isCurrent = validMesaId(selectedId) && String(selectedId) === String(revoked.mesaId);
    if (isCurrent) {
      const m = (mesas || []).find((x) => String(x.id) === String(revoked.mesaId));
      // Lo no comandado queda en el borrador de la mesa (se recupera al
      // volver a entrar con handleCartHistory).
      dispatch(saveHistoryCheckout(revoked.mesaId));
      // Cierra todo lo que colgaba de esta mesa (complementos, peso, notas,
      // comensales, «Otros», cobro…): ver redux/actions/mesaKick.
      dispatch(mesaKicked(revoked.mesaId));
      dispatch(resetCart());
      dispatch(cleanMesa());
      dispatch(setSelectedIdMesaSuccess(-1));
      if (!isGridRoute(pathname)) history.push(postActionRoute(user));

      const mesaLabel = m?.nombre || m?.id_mesa || "";
      enqueueSnackbar(
        revoked.holder
          ? `${holderLabel(revoked.holder)} ha tomado la mesa ${mesaLabel}`.trim()
          : `Has perdido la mesa ${mesaLabel} (sin conexión con el servidor)`.trim(),
        { variant: "warning", preventDuplicate: true },
      );
    }
    dispatch(revokedHandled());
  }, [revoked]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Diálogo "¿Forzar?" ─────────────────────────────────────────────
  const promptMesaLabel = useMemo(() => {
    if (!prompt?.mesaId) return "";
    const m = (mesas || []).find((x) => String(x.id) === String(prompt.mesaId));
    const label = String(m?.nombre || m?.id_mesa || "").trim();
    // Antes «Mesa Mesa 6»: el nombre ya trae «Mesa …» (o «Barra 1», «Terraza 2»).
    // Solo un número suelto se rotula «Mesa N».
    return /^\d+$/.test(label) ? `Mesa ${label}` : label;
  }, [prompt?.mesaId, mesas]);

  // Cancelar: la mesa sigue siendo de quien cobra. En escritorio se vuelve
  // a la mesa en la que estaba (con su cuenta); si no había, al plano limpio.
  const handleCancel = () => {
    // La cuenta pedida con el click (no pintada) ya no se usará.
    discardHeldCheckout();
    const mesaId = cancelTakeover();
    if (!mesaId || String(selectedId) !== String(mesaId)) return;
    const prev = previousMesaAfterCancel({
      previousId: previousSelectedId,
      promptMesaId: mesaId,
      mesas,
      isMobile,
    });
    if (prev) {
      dispatch(setSelectedIdMesaSuccess(prev.id));
      dispatch(getMesaSuccess(prev));
      openMesaCheckout(dispatch, store.getState, prev, { waitLease: whenLeaseSettled });
      return;
    }
    dispatch(resetCart());
    dispatch(cleanMesa());
    dispatch(setSelectedIdMesaSuccess(-1));
    if (!isGridRoute(pathname)) history.push(postActionRoute(user));
  };

  // Forzar: la mesa ya es nuestra y su cuenta se carga por primera vez (con
  // el 409 no se llegó a pintar: ver openMesaCheckout). Si la cuenta sí se
  // estaba viendo, se refresca en el sitio sin vaciar el carrito (antes
  // resetCart + getCheckout → parpadeo).
  const handleForce = async () => {
    const promptMesaId = prompt?.mesaId;
    const res = await forceTakeover();
    if (!res?.ok) {
      enqueueSnackbar("No se ha podido tomar la mesa. Inténtalo de nuevo.", {
        variant: "error",
      });
      return;
    }
    const state = store.getState();
    if (!promptMesaId) return;
    const sel = state?.mesas?.selectedId;
    // Eligió otra mesa mientras tanto: no tocarla.
    if (validMesaId(sel) && String(sel) !== String(promptMesaId)) return;
    const mesa =
      (state.mesas?.mesas || []).find((m) => String(m.id) === String(promptMesaId)) || state.mesas?.mesa;
    if (!mesa || String(mesa.id) !== String(promptMesaId)) return;
    // Algo deseleccionó la mesa con el aviso abierto: se vuelve a ella (sin
    // esto, al forzar no aparecía nada).
    if (!validMesaId(sel)) {
      dispatch(setSelectedIdMesaSuccess(mesa.id));
      dispatch(getMesaSuccess(mesa));
    }
    refreshMesaCheckoutInPlace(dispatch, store.getState, mesa);
  };

  const holder = prompt?.holder;
  return (
    <Dialog open={!!prompt} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PointOfSaleIcon color="warning" />
        {promptMesaLabel || "Mesa"} cobrándose
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {holder?.employeeName || "Otro camarero"} está cobrando esta mesa
          {holder?.deviceLabel ? ` en ${holder.deviceLabel}` : " en otro dispositivo"}.
          Si fuerzas, su cobro se interrumpe en su pantalla. ¿Forzar?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancelar</Button>
        <Button color="warning" variant="contained" onClick={handleForce}>
          Forzar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
